import React, { useEffect, useState, useMemo, useDeferredValue, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SchedulingFreeBusyGrid from './SchedulingFreeBusyGrid';
import { useAuthStore } from '../store/authStore';
import { p2pChallengesService } from '../services/p2pChallengesService';
import { groupSlotsIntoRanges, type GroupedTimeRange } from '../utils/slotGrouping';

/** Inputs required to render and operate the P2P scheduling workflow. */
interface ScheduleProposalModalP2PProps {
  isOpen: boolean;
  opponentId: string;
  proposalId?: string; // For counter-propose/confirm flows
  // Preloaded data from parent
  initialParticipants?: Participant[];
  initialProposal?: ProposalData | null;
  initialViewingTimezone?: string;
  initialDisplayDateStart?: Date;
  /** Canonical UTC slots selected when opening a new proposal. */
  initialSelectedSlots?: string[];
  initialScrollToHour?: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Public profile and availability data used by the free/busy grid. */
interface Participant {
  id: string;
  nickname: string;
  timezone: string;
  timezone_offset?: string;
  availability_schedule?: Record<string, Array<{ start: string; end: string }>>;
}

/** Proposal detail returned by the challenge API. */
interface ProposalData {
  id: string;
  proposed_by_user_id: string;
  proposed_at: string;
  status: string;
  notes?: string;
  slots: Array<{
    id: string;
    slot_datetime: string;
    status: string;
  }>;
  confirmations: Array<{ user_id: string; confirmed_at: string }>;
}

/** Return only the slots that represent the proposal's current schedule. */
const getVisibleProposalSlots = (proposal: ProposalData | null) => {
  if (!proposal?.slots) return [];
  const visibleStatus = proposal.status === 'confirmed' ? 'confirmed' : 'pending';
  return proposal.slots.filter((slot) => slot.status === visibleStatus);
};

/** Defer range grouping so large slot selections do not block grid interaction. */
const useAsyncGroupedRanges = (slotDatetimes: string[], timezone: string): GroupedTimeRange[] => {
  const deferredSlotDatetimes = useDeferredValue(slotDatetimes);
  const [ranges, setRanges] = useState<GroupedTimeRange[]>([]);

  useEffect(() => {
    let cancelled = false;

    const compute = () => {
      if (cancelled) return;
      setRanges(groupSlotsIntoRanges(deferredSlotDatetimes, timezone));
    };

    const win = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (typeof win.requestIdleCallback === 'function') {
      const handle = win.requestIdleCallback(compute, { timeout: 120 });
      return () => {
        cancelled = true;
        if (typeof win.cancelIdleCallback === 'function') {
          win.cancelIdleCallback(handle);
        }
      };
    }

    const timer = window.setTimeout(compute, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [deferredSlotDatetimes, timezone]);

  return ranges;
};

/** Render proposal, confirmation, counter-proposal, edit, and cancellation actions. */
export default function ScheduleProposalModalP2P({
  isOpen,
  opponentId,
  proposalId,
  initialParticipants,
  initialProposal,
  initialViewingTimezone,
  initialDisplayDateStart,
  initialSelectedSlots,
  initialScrollToHour,
  onClose,
  onSuccess
}: ScheduleProposalModalP2PProps) {
  const { t, i18n } = useTranslation();
  const { userId } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [participants, setParticipants] = useState<Participant[]>(initialParticipants || []);
  const [reservedSlots, setReservedSlots] = useState<Record<string, 'p2p' | 'tournament'>>({});
  const [pendingSlots, setPendingSlots] = useState<Record<string, 'p2p' | 'tournament'>>({});
  const [viewingTimezone, setViewingTimezone] = useState(initialViewingTimezone || 'UTC');
  const [proposal, setProposal] = useState<ProposalData | null>(initialProposal || null);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [mode, setMode] = useState<'propose' | 'confirm' | 'counter' | 'edit_proposal'>('propose');
  const [displayDateStart, setDisplayDateStart] = useState<Date>(initialDisplayDateStart || new Date());
  const [scrollToHour, setScrollToHour] = useState<number | null>(initialScrollToHour || null);
  // For slot-level confirmation: track which slots user has explicitly selected
  const [confirmedSlotIds, setConfirmedSlotIds] = useState<Set<string>>(new Set());
  const [hasStartedConfirmationSelection, setHasStartedConfirmationSelection] = useState(false);
  const hasStartedConfirmationSelectionRef = useRef(false);
  // For edit_proposal mode: track if user has clicked any slot yet
  const [hasStartedEditSelection, setHasStartedEditSelection] = useState(false);
  const hasStartedEditSelectionRef = useRef(false);

  // Update state when preloaded data props change
  useEffect(() => {
    if (!isOpen) return;
    
    if (initialParticipants) {
      setParticipants(initialParticipants);
    }
    
    if (initialViewingTimezone) {
      setViewingTimezone(initialViewingTimezone);
    }
    
    if (initialDisplayDateStart) {
      setDisplayDateStart(initialDisplayDateStart);
    }
    
    if (initialScrollToHour !== null && initialScrollToHour !== undefined) {
      setScrollToHour(initialScrollToHour);
    }
    
    if (initialProposal) {
      setProposal(initialProposal);
      setNotes(initialProposal.notes || '');
    } else {
      // Clear proposal data when opening modal for new schedule (no existing proposal)
      setProposal(null);
      // Preserve the caller's suggested slot so the form can be submitted
      // immediately, while still allowing the proposer to change it.
      setSelectedSlots(new Set(initialSelectedSlots || []));
      setNotes('');
    }
  }, [isOpen, initialParticipants, initialProposal, initialViewingTimezone, initialDisplayDateStart, initialSelectedSlots, initialScrollToHour]);

  // Load conflicts shared by both players so the grid blocks overlapping P2P
  // and tournament proposals. The current proposal is excluded when editing.
  useEffect(() => {
    if (!isOpen || participants.length === 0) return;
    let cancelled = false;

    p2pChallengesService
      .getOccupiedSlots(participants.map((participant) => participant.id), proposal?.id)
      .then((response) => {
        if (cancelled) return;
        const nextReserved: Record<string, 'p2p' | 'tournament'> = {};
        const nextPending: Record<string, 'p2p' | 'tournament'> = {};
        for (const conflict of response.conflicts || []) {
          const slotKey = new Date(conflict.slot_datetime).toISOString();
          if (conflict.status === 'confirmed') nextReserved[slotKey] = conflict.source;
          else nextPending[slotKey] = conflict.source;
        }
        setPendingSlots(nextPending);
        setReservedSlots(nextReserved);
        // A suggested slot may already be confirmed by another proposal. Drop
        // only those suggestions; pending proposals remain selectable.
        setSelectedSlots((previous) => new Set([...previous].filter((slot) => !nextReserved[slot])));
        setConfirmedSlotIds((previous) => new Set([...previous].filter((slot) => !nextReserved[slot])));
      })
      .catch((error) => {
        console.error('Error loading occupied scheduling slots:', error);
        if (!cancelled) {
          setReservedSlots({});
          setPendingSlots({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, participants, proposal?.id]);

  // Initialize mode based on preloaded proposal data
  useEffect(() => {
    if (!isOpen) return;

    if (proposal) {
      // A confirmed proposal can be moved by either participant. While pending,
      // only the proposer edits; the challenged player confirms or counters.
      if (proposal.status === 'confirmed' || (userId && proposal.proposed_by_user_id === userId)) {
        setMode('edit_proposal');
        // Pre-fill with current slots so proposer can modify them
        if (proposal.slots) {
          const proposedSlotDatetimes = getVisibleProposalSlots(proposal).map(s => s.slot_datetime);
          setSelectedSlots(new Set(proposedSlotDatetimes));
        } else {
          setSelectedSlots(new Set());
        }
        // Load existing notes for editing
        setNotes(proposal.notes || '');
        // Reset edit selection flag - show original slots in blue
        hasStartedEditSelectionRef.current = false;
        setHasStartedEditSelection(false);
      } else {
        setMode('confirm');
        // Pre-select proposed slots for opponent to confirm or modify
        if (proposal.slots) {
          const proposedSlotDatetimes = getVisibleProposalSlots(proposal).map(s => s.slot_datetime);
          // Initialize confirmedSlotIds with all proposed slots (all checked by default)
          setConfirmedSlotIds(new Set(proposedSlotDatetimes));
          setHasStartedConfirmationSelection(false);
          hasStartedConfirmationSelectionRef.current = false;
        }
      }
    } else {
      setMode('propose');
      // Keep the caller-provided suggestion after mode initialization; this
      // effect runs after the preload effect and must not clear the proposal.
      setSelectedSlots(new Set(initialSelectedSlots || []));
      setHasStartedConfirmationSelection(false);
      hasStartedConfirmationSelectionRef.current = false;
    }
  }, [isOpen, proposal, userId, initialSelectedSlots]);

  const handleSlotToggle = useCallback((slotDatetime: string, selected: boolean) => {
    // In edit_proposal mode, first click triggers showing only selected slots
    if (mode === 'edit_proposal' && !hasStartedEditSelectionRef.current) {
      hasStartedEditSelectionRef.current = true;
      setHasStartedEditSelection(true);
    }
    
    setSelectedSlots((prevSelected) => {
      const nextSelected = new Set(prevSelected);
      if (selected) {
        nextSelected.add(slotDatetime);
      } else {
        nextSelected.delete(slotDatetime);
      }
      return nextSelected;
    });
  }, [mode]);

  /**
   * In confirm mode: first click deselects all others and keeps only this one
   * Subsequent clicks: normal toggle
   */
  const handleConfirmSlotToggle = useCallback((slotDatetime: string, selected: boolean) => {
    if (!hasStartedConfirmationSelectionRef.current) {
      // First click: clear all and select only this one
      hasStartedConfirmationSelectionRef.current = true;
      setHasStartedConfirmationSelection(true);
      setConfirmedSlotIds(new Set([slotDatetime]));
    } else {
      // Subsequent clicks: normal toggle
      setConfirmedSlotIds((prevConfirmed) => {
        const nextConfirmed = new Set(prevConfirmed);
        if (selected) {
          nextConfirmed.add(slotDatetime);
        } else {
          nextConfirmed.delete(slotDatetime);
        }
        return nextConfirmed;
      });
    }
  }, []);

  const handleProposeSlots = async () => {
    if (selectedSlots.size === 0) {
      setError(t('schedule_modal.error_select_slot'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const slotArray = Array.from(selectedSlots);

      if (mode === 'counter') {
        // Counter-propose on existing proposal
        if (!proposal) {
          setError(t('schedule_modal.error_no_proposal'));
          return;
        }
        const response = await p2pChallengesService.counterPropose(
          proposal.id,
          slotArray,
          notes
        );
        if (response.success || response.proposalId) {
          onSuccess?.();
          onClose();
        }
      } else if (mode === 'edit_proposal') {
        // Update existing proposal (proposer editing their own proposal)
        if (!proposal) {
          setError(t('schedule_modal.error_no_proposal'));
          return;
        }
        const response = await p2pChallengesService.updateProposal(
          proposal.id,
          slotArray,
          notes
        );
        if (response.success) {
          onSuccess?.();
          onClose();
        }
      } else {
        // Initial challenge proposal
        const response = await p2pChallengesService.proposeChallenge(
          opponentId,
          slotArray,
          notes
        );

        if (response.success || response.proposalId) {
          onSuccess?.();
          onClose();
        }
      }
    } catch (err: any) {
      console.error('Error proposing challenge:', err);
      // Preserve localized 429 details, including the profile-timezone retry time.
      setError(err.response?.data?.error || t('schedule_modal.error_propose'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSlots = async () => {
    if (!proposal) {
      setError(t('schedule_modal.error_no_proposal'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Convert confirmed slot datetimes back to their slot IDs for backend
      let slotIdsToSend: string[] = [];
      if (mode === 'confirm') {
        // Map datetimes back to slot IDs from proposal
        slotIdsToSend = proposal.slots
          .filter(s => confirmedSlotIds.has(s.slot_datetime))
          .map(s => s.id);
      } else {
        // In propose mode, selectedSlots are datetimes, but we need to convert
        // This shouldn't happen for confirm, but handle it for consistency
        slotIdsToSend = proposal.slots
          .filter(s => selectedSlots.has(s.slot_datetime))
          .map(s => s.id);
      }

      const response = await p2pChallengesService.confirmSlots(proposal.id, slotIdsToSend);

      if (response.success) {
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.error('Error confirming slots:', err);
      // Keep the modal open: the success callback may unmount it and hide the
      // reservation conflict, including the affected slots returned by the API.
      setError((err as any).response?.data?.error || t('schedule_modal.error_confirm'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelProposal = async () => {
    if (!proposal) {
      setError(t('schedule_modal.error_no_proposal'));
      return;
    }

    if (!confirm(t('schedule_modal.confirm_cancel_proposal'))) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await p2pChallengesService.cancelProposal(proposal.id);
      
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error canceling proposal:', err);
      setError(t('schedule_modal.error_cancel_proposal'));
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value.slice(0, 500));
  }, []);

  const selectedRangeDatetimes = useMemo(
    () => (mode === 'confirm' ? Array.from(confirmedSlotIds) : Array.from(selectedSlots)),
    [mode, confirmedSlotIds, selectedSlots]
  );
  const selectedRanges = useAsyncGroupedRanges(selectedRangeDatetimes, viewingTimezone);
  const visibleProposalSlots = useMemo(() => getVisibleProposalSlots(proposal), [proposal]);
  const proposalRanges = useMemo(
    () => (visibleProposalSlots.length ? groupSlotsIntoRanges(visibleProposalSlots.map(s => s.slot_datetime), viewingTimezone) : []),
    [visibleProposalSlots, viewingTimezone]
  );
  const proposalStatusesByRange = useMemo(
    () =>
      proposalRanges.map((range) =>
        visibleProposalSlots
          .filter(s => {
            const slotDate = new Date(s.slot_datetime);
            return slotDate >= range.start && slotDate < range.end;
          })
          .map(s => t(`schedule_modal.slot_status_${s.status}`, s.status))
          .join(', ')
      ),
    [visibleProposalSlots, proposalRanges]
  );

  // Keep these hooks before the closed-state return so the hook order remains
  // identical when the modal opens and closes.
  const dateEnd = useMemo(() => {
    const end = new Date(displayDateStart);
    end.setDate(end.getDate() + 14);
    return end;
  }, [displayDateStart]);

  const proposedSlotDatetimes = useMemo(
    () => visibleProposalSlots.map((slot) => slot.slot_datetime),
    [visibleProposalSlots]
  );
  const confirmedSlotsMap = useMemo<Record<string, string[]>>(() => ({}), []);
  const minimumDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: viewingTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div data-help-id="region-p2p-schedule-modal" className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex justify-between items-start">
            <h2 className="text-2xl font-bold text-gray-800">
              {mode === 'propose' ? t('schedule_modal.title_propose') : mode === 'edit_proposal' ? t('schedule_modal.title_edit') : t('schedule_modal.title_confirm')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            >
              ✕
            </button>
          </div>
          {error && (
            <div role="alert" className="mt-4 p-3 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {loading && participants.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">{t('schedule_modal.loading')}</p>
            </div>
          ) : (
            <>
              {/* Date picker */}
              <div data-help-id="region-p2p-schedule-date-range" className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">{t('schedule_modal.date_range_start')}</label>
                <div className="flex gap-2">
                  <input
                    data-help-id="field-p2p-schedule-start-date"
                    type="date"
                    min={minimumDate}
                    value={displayDateStart.toISOString().split('T')[0]}
                    onChange={(e) => setDisplayDateStart(new Date(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  <span className="text-sm text-gray-500 self-center">
                    {t('schedule_modal.next_14_days', { date: dateEnd.toLocaleDateString(i18n.language || 'en-US') })}
                  </span>
                  <span className="text-sm text-gray-600 self-center font-semibold">
                    | {t('schedule_modal.viewing_timezone', { timezone: viewingTimezone })}
                  </span>
                </div>
              </div>

              {/* Grid */}
              <div data-help-id="region-p2p-availability-grid" className="space-y-2">
                <h3 className="font-semibold text-gray-800">{t('schedule_modal.availability_grid')}</h3>
                <SchedulingFreeBusyGrid
                  participants={participants}
                  dateStart={displayDateStart}
                  dateEnd={dateEnd}
                  selectedSlots={mode === 'confirm' ? confirmedSlotIds : selectedSlots}
                  onSlotToggle={mode === 'confirm' ? handleConfirmSlotToggle : handleSlotToggle}
                  readOnly={false}
                  proposedSlots={mode === 'edit_proposal' && !hasStartedEditSelection ? proposedSlotDatetimes : []}
                  confirmedSlots={confirmedSlotsMap}
                  reservedSlots={reservedSlots}
                  pendingSlots={pendingSlots}
                  viewingTimezone={viewingTimezone}
                  scrollToHour={scrollToHour}
                  confirmMode={mode === 'confirm'}
                  hasStartedConfirmationSelection={hasStartedConfirmationSelection}
                />
              </div>

              {/* Selected slots summary */}
              {((mode === 'confirm' && confirmedSlotIds.size > 0) || (mode !== 'confirm' && selectedSlots.size > 0)) && (
                <div data-help-id="region-p2p-selected-slots" className={`p-3 rounded ${mode === 'confirm' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm font-semibold ${mode === 'confirm' ? 'text-green-900' : 'text-blue-900'}`}>
                    {mode === 'confirm' 
                      ? t('schedule_modal.slots_to_confirm', { count: confirmedSlotIds.size })
                      : t('schedule_modal.slots_selected', { count: selectedSlots.size })
                    }
                  </p>
                  <div className="mt-3 space-y-2">
                    {selectedRanges.map((range, idx) => (
                      <div key={idx} className={`text-sm p-2 bg-white rounded border ${mode === 'confirm' ? 'text-green-800 border-green-100' : 'text-blue-800 border-blue-100'}`}>
                        <div className="font-semibold">
                          {range.start.toLocaleDateString(i18n.language || 'en-US', { timeZone: viewingTimezone })} - {range.hours}
                        </div>
                        <div className="text-xs text-gray-600">
                          {viewingTimezone}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Proposal info (if responding) */}
              {proposal && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm font-semibold text-yellow-900">{t('schedule_modal.active_proposal')}</p>
                  <p className="text-xs text-yellow-800 mt-1">
                    {t('schedule_modal.proposed_at', { date: new Date(proposal.proposed_at).toLocaleString(i18n.language || 'en-US', { timeZone: viewingTimezone }) })}
                  </p>
                  {proposal.notes && (
                    <p className="text-xs text-yellow-800 mt-2 italic">
                      {t('schedule_modal.notes_value', { notes: proposal.notes })}
                    </p>
                  )}
                  {visibleProposalSlots.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-yellow-900 mb-2">{t('schedule_modal.proposed_slots')}</p>
                      <div className="space-y-1">
                        {proposalRanges.map((range, idx) => (
                          <div key={idx} className="text-xs text-yellow-800 bg-white rounded px-2 py-1 border border-yellow-100">
                            <div className="font-semibold">
                              {range.start.toLocaleDateString(i18n.language || 'en-US', { timeZone: viewingTimezone })} - {range.hours}
                            </div>
                            <div className="text-xs text-gray-600">
                              ({proposalStatusesByRange[idx]})
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes textarea */}
              {mode === 'confirm' ? (
                // In confirm mode, show the proposer's notes as read-only
                proposal?.notes && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm font-semibold text-blue-900">{t('schedule_modal.proposer_notes')}</p>
                    <p className="text-sm text-blue-800 mt-2 whitespace-pre-wrap">{proposal.notes}</p>
                  </div>
                )
              ) : (
                // In propose/edit mode, show textarea for editing notes
                <div data-help-id="region-p2p-schedule-notes" className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-800">
                    {t('schedule_modal.notes_label')}
                  </label>
                  <textarea
                    data-help-id="field-p2p-schedule-notes"
                    value={notes}
                    onChange={handleNotesChange}
                    placeholder={t('schedule_modal.notes_placeholder')}
                    className="w-full p-3 border border-gray-300 rounded text-sm font-mono"
                    rows={3}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">{notes.length}/500</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div data-help-id="region-p2p-schedule-footer" className="sticky bottom-0 bg-white border-t border-gray-200 p-6 space-y-3">
          <div className="flex gap-3 justify-end">
            <button
              data-help-id="action-p2p-cancel-schedule"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700 font-medium"
              disabled={loading}
            >
              {t('cancel')}
            </button>

            {mode === 'propose' ? (
              <button
                data-help-id="action-p2p-propose-schedule"
                onClick={handleProposeSlots}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
                disabled={loading || selectedSlots.size === 0}
              >
                {loading ? t('schedule_modal.proposing') : t('schedule_modal.propose_selected')}
              </button>
            ) : mode === 'edit_proposal' ? (
              <>
              <button
                data-help-id="action-p2p-cancel-proposal"
                  onClick={handleCancelProposal}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
                  disabled={loading}
                >
                  {t('schedule_modal.cancel_proposal')}
                </button>
                <button
                  data-help-id="action-p2p-change-proposal"
                  onClick={handleProposeSlots}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
                  disabled={loading || selectedSlots.size === 0}
                >
                  {loading ? t('schedule_modal.updating') : t('schedule_modal.change_proposal')}
                </button>
              </>
            ) : (
              <>
                <button
                  data-help-id="action-p2p-reject-proposal"
                  onClick={handleCancelProposal}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
                  disabled={loading}
                >
                  {loading ? t('schedule_modal.rejecting') : `❌ ${t('schedule_modal.reject')}`}
                </button>
                <button
                  data-help-id="action-p2p-counter-propose"
                  onClick={() => {
                    setMode('counter');
                    setSelectedSlots(new Set());
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700 font-medium"
                  disabled={loading}
                >
                  🔄 {t('schedule_modal.counter_propose')}
                </button>
                <button
                  data-help-id="action-p2p-confirm-schedule"
                  onClick={handleConfirmSlots}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium disabled:opacity-50"
                  disabled={loading || confirmedSlotIds.size === 0}
                >
                  {loading ? t('schedule_modal.confirming') : `✅ ${t('schedule_modal.confirm_slots', { count: confirmedSlotIds.size })}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
